import { useMemo, useState } from 'react';
import {
  Box, Checkbox, Divider, FormControl, InputAdornment, InputLabel,
  ListItemText, ListSubheader, MenuItem, Select, TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { NO_SELECTION, isNoSelection } from '../../utils/dashboardUtils';

export const MENU_PAPER_LIGHT = {
  autoFocus: false,
  PaperProps: {
    sx: {
      bgcolor: '#f5f7fb',
      color: '#1a1a2e',
      maxHeight: 380,
      '& .MuiMenuItem-root': {
        color: '#1a1a2e',
        '&:hover': { bgcolor: '#e8edf7' },
      },
      '& .MuiCheckbox-root': { color: '#4863A0', '&.Mui-checked': { color: '#4863A0' } },
      '& .MuiListItemText-primary': { color: '#1a1a2e' },
      '& .MuiListSubheader-root': { bgcolor: '#f5f7fb', color: '#1a1a2e' },
    },
  },
};

export const MENU_PAPER_PANEL = (colors) => ({
  autoFocus: false,
  PaperProps: {
    sx: {
      bgcolor: colors.primary[300] || colors.primary[400],
      color: colors.grey[100],
      maxHeight: 380,
      border: `1px solid ${colors.primary[500]}`,
      '& .MuiMenuItem-root': {
        color: colors.grey[100],
        '&:hover': { bgcolor: colors.primary[500] },
      },
      '& .MuiCheckbox-root': { color: colors.greenAccent[500], '&.Mui-checked': { color: colors.greenAccent[400] } },
      '& .MuiListSubheader-root': { bgcolor: colors.primary[300] || colors.primary[400], color: colors.grey[100] },
    },
  },
});

const toEffectiveSelection = (selected, allValues) => {
  if (!allValues.length) return [];
  if (isNoSelection(selected)) return [];
  if (!selected.length || allValues.every((v) => selected.includes(v))) return [...allValues];
  return selected.filter((v) => allValues.includes(v));
};

const toParentSelection = (effective, allValues) => {
  if (!allValues.length) return isNoSelection(effective) ? [NO_SELECTION] : [];
  if (!effective.length) return [NO_SELECTION];
  if (effective.length >= allValues.length) return [];
  return effective;
};

const DashboardMultiSelect = ({
  label, value = [], options = [], onChange, minWidth = 120, sx = {},
  menuProps = MENU_PAPER_LIGHT, enableSearch = true, hideLabel = false,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const allValues = useMemo(() => options.map((o) => o.value), [options]);
  const effectiveSelected = useMemo(
    () => toEffectiveSelection(selected, allValues),
    [selected, allValues],
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, search]);

  const filteredValues = useMemo(
    () => filteredOptions.map((o) => o.value),
    [filteredOptions],
  );

  const isFilteredAllSelected = filteredValues.length > 0
    && filteredValues.every((v) => effectiveSelected.includes(v));

  const isFilteredPartial = !isFilteredAllSelected && filteredValues.some(
    (v) => effectiveSelected.includes(v),
  );

  const handleClose = () => {
    setOpen(false);
    setSearch('');
  };

  const commitSelection = (nextEffective) => {
    onChange(toParentSelection(nextEffective, allValues));
  };

  const handleSelectAll = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (search.trim()) {
      if (isFilteredAllSelected) {
        commitSelection(effectiveSelected.filter((v) => !filteredValues.includes(v)));
      } else {
        commitSelection([...new Set([...effectiveSelected, ...filteredValues])]);
      }
      return;
    }

    if (isFilteredAllSelected) {
      commitSelection([]);
      return;
    }

    commitSelection([...allValues]);
  };

  const handleToggleItem = (val, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (effectiveSelected.includes(val)) {
      commitSelection(effectiveSelected.filter((v) => v !== val));
    } else {
      commitSelection([...effectiveSelected, val]);
    }
  };

  const renderValue = () => {
    if (isNoSelection(selected)) return 'None';
    if (!selected.length) return 'All';
    if (selected.length === 1) {
      const opt = options.find((o) => o.value === selected[0]);
      return opt?.label ?? selected[0];
    }
    return `${selected.length} selected`;
  };

  const mergedMenuProps = {
    ...menuProps,
    autoFocus: false,
    PaperProps: {
      ...menuProps.PaperProps,
      sx: {
        ...menuProps.PaperProps?.sx,
        maxHeight: 380,
      },
    },
  };

  return (
    <FormControl size="small" sx={{ minWidth, ...sx }}>
      {!hideLabel && <InputLabel sx={{ color: 'inherit' }}>{label}</InputLabel>}
      <Select
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        value={effectiveSelected}
        label={hideLabel ? undefined : label}
        displayEmpty={hideLabel}
        onChange={() => {}}
        renderValue={renderValue}
        MenuProps={mergedMenuProps}
        inputProps={hideLabel ? { 'aria-label': label } : undefined}
        sx={{
          color: 'inherit',
          '.MuiOutlinedInput-notchedOutline': { borderColor: 'inherit' },
          '.MuiSvgIcon-root': { color: 'inherit' },
        }}
      >
        {enableSearch && (
          <ListSubheader sx={{ bgcolor: 'inherit', lineHeight: 'normal', py: 1, px: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={search}
              onChange={(ev) => setSearch(ev.target.value)}
              onKeyDown={(ev) => ev.stopPropagation()}
              onClick={(ev) => ev.stopPropagation()}
              onMouseDown={(ev) => ev.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#4863A0' }} />
                  </InputAdornment>
                ),
                sx: { bgcolor: '#fff', fontSize: 13 },
              }}
            />
          </ListSubheader>
        )}

        <MenuItem
          dense
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSelectAll}
          sx={{ fontWeight: 600 }}
        >
          <Checkbox
            size="small"
            checked={isFilteredAllSelected}
            indeterminate={isFilteredPartial}
            tabIndex={-1}
            disableRipple
          />
          <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {filteredOptions.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            dense
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => handleToggleItem(opt.value, e)}
          >
            <Checkbox
              size="small"
              checked={effectiveSelected.includes(opt.value)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>
        ))}

        {!filteredOptions.length && (
          <Box px={2} py={1}>
            <ListItemText primary="No matches" primaryTypographyProps={{ fontSize: 13, color: '#666' }} />
          </Box>
        )}
      </Select>
    </FormControl>
  );
};

export default DashboardMultiSelect;
